import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Key used to mark that this tab has already attempted one auto-reload.
// sessionStorage survives `window.location.reload()` but is cleared when
// the tab is closed — exactly the lifetime we want.
// Value is the timestamp (ms) of the reload attempt; flags older than
// RELOAD_TTL_MS are treated as expired so transient HMR failures don't
// permanently lock out users.
const RELOAD_FLAG = 'xavior:chunk-reload-attempted';
const RELOAD_TTL_MS = 30_000;

// Module-local backstop. If sessionStorage is unavailable (private mode,
// quota exceeded, storage blocked), this in-memory flag at least prevents
// the same page load from triggering multiple reloads.
let inMemoryReloadFired = false;

function canUseSessionStorage(): boolean {
  try {
    const testKey = 'xavior:__storage_test__';
    sessionStorage.setItem(testKey, '1');
    const ok = sessionStorage.getItem(testKey) === '1';
    sessionStorage.removeItem(testKey);
    return ok;
  } catch {
    return false;
  }
}

function hasAttemptedReload(): boolean {
  if (inMemoryReloadFired) return true;
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return false;
    // Treat the flag as expired if the reload attempt was too long ago.
    // This lets the page recover after a transient HMR/network failure.
    return Date.now() - Number(raw) < RELOAD_TTL_MS;
  } catch {
    return false;
  }
}

function markReloadAttempted(): void {
  inMemoryReloadFired = true;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // best-effort
  }
}

/**
 * Reload the current page exactly once per tab session to recover from a
 * stale-chunk failure after an atomic deploy. Skips the reload entirely if:
 *   - we've already reloaded this tab session (prevents loops), or
 *   - sessionStorage is unavailable (can't detect loops across reloads).
 * In the skip case, callers should surface an error so an ErrorBoundary can
 * show a manual-refresh UI instead.
 *
 * Returns `true` if a reload was triggered, `false` if it was skipped.
 */
function reloadOnce(): boolean {
  if (typeof window === 'undefined') return false;
  if (hasAttemptedReload()) return false;
  if (!canUseSessionStorage()) {
    // Can't persist the reload flag across reloads → would infinite-loop.
    // Mark in-memory so we don't re-check repeatedly this page load.
    inMemoryReloadFired = true;
    return false;
  }
  markReloadAttempted();
  window.location.reload();
  return true;
}

/**
 * React.lazy wrapper that self-heals after an atomic deploy.
 *
 * Cloudflare Pages (and Netlify, Vercel) replace hashed chunks on every
 * deploy. Tabs that loaded the old HTML still reference the old chunk
 * filenames; dynamic imports for them 404 and React surfaces an opaque
 * TypeError via Suspense. This wrapper:
 *   1. Attempts the import.
 *   2. On failure, tries to reload the page once per tab session so the
 *      browser fetches fresh HTML with the new chunk hashes.
 *   3. If reload is suppressed (already attempted, or storage unavailable),
 *      rethrows so an ErrorBoundary can render a manual-refresh CTA.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      console.warn('[lazyWithRetry] dynamic import failed', err);
      if (reloadOnce()) {
        // A reload was triggered. Return a never-resolving promise so
        // Suspense stays in the fallback state while the page navigates.
        return new Promise<{ default: T }>(() => {});
      }
      // Reload suppressed (already attempted this session, or storage
      // unavailable). Rethrow so an ErrorBoundary can handle it.
      throw err;
    }
  });
}

/**
 * Install a global listener for Vite's `vite:preloadError` event (fired when
 * a `<link rel="modulepreload">` preload fails). Reloads once per session;
 * the listener calls `preventDefault()` to suppress Vite's default re-throw
 * once a reload is in flight.
 */
export function installChunkErrorHandler(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    console.warn('[vite:preloadError]', payload);
    if (reloadOnce()) {
      event.preventDefault();
    }
  });
}

// Exposed for testing only.
export const __internals = {
  resetInMemoryFlag: () => {
    inMemoryReloadFired = false;
  },
};
